import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Heart, Users, Sparkles, ArrowRight, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PropertyCard } from "@/components/site/PropertyCard";

const EVENT_TYPES = ["Wedding", "Bachelorette Party", "Bachelor Party", "Family Reunion", "Corporate Retreat", "Birthday / Milestone", "Other Celebration"];

export default function Weddings() {
  const [props, setProps] = useState([]);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", event_type: "Wedding",
    event_date: "", guest_count: 50, property_interest: "", message: "",
  });
  const [sent, setSent] = useState(false);

  useEffect(() => {
    api.get("/properties?amenity=events").then((r) => {
      const order = { wedding: 0, gathering: 1 };
      setProps([...r.data.properties].sort((a, b) => (order[a.event_type] ?? 9) - (order[b.event_type] ?? 9)));
    }).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return toast.error("Name and email are required");
    try {
      await api.post("/event-leads", { ...form, guest_count: Number(form.guest_count) || 0 });
      setSent(true);
      toast.success("Thank you! We'll be in touch to help plan your celebration.");
    } catch {
      toast.error("Could not submit. Please try again.");
    }
  };

  const hero = props.find((p) => p.event_type === "wedding")?.hero;

  return (
    <div>
      {/* HERO */}
      <div className="relative min-h-[62vh] w-full overflow-hidden">
        {hero && <img src={hero} alt="Wedding & event venue" className="absolute inset-0 h-full w-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/30" />
        <div className="relative z-10 mx-auto flex min-h-[62vh] max-w-7xl flex-col justify-center px-5 py-20 lg:px-8">
          <span className="overline text-gold">Weddings & Celebrations</span>
          <h1 className="mt-4 max-w-3xl text-4xl font-medium leading-tight text-white sm:text-5xl lg:text-6xl">
            Where your biggest moments come together.
          </h1>
          <p className="mt-5 max-w-xl text-lg text-white/80">
            Weddings, bachelorette & bachelor parties, family reunions, corporate retreats and milestone birthdays — hosted in estates designed for celebration, right on the water in the Texas Hill Country.
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            {["Weddings", "Bachelorette Parties", "Bachelor Parties", "Family Reunions", "Corporate Retreats", "Birthdays"].map((x) => (
              <span key={x} className="rounded-full border border-white/30 bg-white/10 px-3 py-1 text-xs font-medium text-white backdrop-blur">{x}</span>
            ))}
          </div>
          <div className="mt-8">
            <a href="#inquire">
              <Button data-testid="wed-hero-cta" className="rounded-full bg-white px-7 text-ink hover:bg-camel hover:text-white">
                Start planning <ArrowRight size={18} className="ml-1" />
              </Button>
            </a>
          </div>
        </div>
      </div>

      {/* VALUE ROW */}
      <div className="mx-auto max-w-7xl px-5 py-14 lg:px-8">
        <div className="grid gap-6 md:grid-cols-3">
          {[
            { icon: Heart, t: "Weddings & receptions", d: "Ceremonies, receptions and a place for the whole party to stay the night — all in one stunning waterfront setting." },
            { icon: Users, t: "Groups & reunions", d: "Bachelorette and bachelor parties, family reunions and big birthdays — plenty of room to bring everyone together." },
            { icon: Sparkles, t: "Corporate retreats", d: "Offsites, team retreats and planning sessions with space to work, unwind, and connect." },
          ].map((c) => (
            <div key={c.t} className="rounded-3xl card-lux p-7">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-cream text-camel"><c.icon size={22} strokeWidth={1.5} /></span>
              <h3 className="mt-4 text-xl text-ink">{c.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-taupe">{c.d}</p>
            </div>
          ))}
        </div>
      </div>

      {/* EVENT HOMES */}
      <div className="mx-auto max-w-7xl px-5 pb-4 lg:px-8">
        <span className="overline">Our event-ready homes</span>
        <h2 className="mt-2 text-3xl lg:text-4xl">Estates built for gatherings</h2>
        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="event-homes-grid">
          {props.map((p, i) => <PropertyCard key={p.id} p={p} index={i} />)}
          {props.length === 0 && <p className="text-taupe">Loading our event homes…</p>}
        </div>
      </div>

      {/* INQUIRY FORM */}
      <div id="inquire" className="mt-16 bg-cream py-16">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <div className="text-center">
            <span className="overline">Tell us about your event</span>
            <h2 className="mt-2 text-3xl lg:text-4xl">Request an event inquiry</h2>
            <p className="mt-3 text-taupe">Share a few details and our team will help you find the perfect home and plan the day.</p>
          </div>

          <div className="mt-8 rounded-3xl card-lux p-7">
            {sent ? (
              <div data-testid="event-lead-success" className="rounded-2xl bg-white p-8 text-center">
                <Heart className="mx-auto mb-3 text-camel" />
                <p className="text-lg text-ink">Inquiry received ✦</p>
                <p className="mt-1 text-sm text-taupe">A member of our team will reach out shortly to help plan your celebration.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                <Input data-testid="event-name" placeholder="Full name" value={form.name}
                  onChange={(e) => set("name", e.target.value)} className="rounded-2xl border-black/10" />
                <div className="grid gap-4 sm:grid-cols-2">
                  <Input data-testid="event-email" type="email" placeholder="Email" value={form.email}
                    onChange={(e) => set("email", e.target.value)} className="rounded-2xl border-black/10" />
                  <Input data-testid="event-phone" placeholder="Phone" value={form.phone}
                    onChange={(e) => set("phone", e.target.value)} className="rounded-2xl border-black/10" />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="overline mb-2 block">Event type</label>
                    <Select value={form.event_type} onValueChange={(v) => set("event_type", v)}>
                      <SelectTrigger data-testid="event-type" className="rounded-2xl border-black/10"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {EVENT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="overline mb-2 block">Home of interest</label>
                    <Select value={form.property_interest || "unsure"} onValueChange={(v) => set("property_interest", v === "unsure" ? "" : v)}>
                      <SelectTrigger data-testid="event-property" className="rounded-2xl border-black/10"><SelectValue placeholder="Not sure yet" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unsure">Not sure yet</SelectItem>
                        {props.map((p) => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="overline mb-2 block">Event date</label>
                    <Input data-testid="event-date" type="date" value={form.event_date}
                      onChange={(e) => set("event_date", e.target.value)} className="rounded-2xl border-black/10" />
                  </div>
                  <div>
                    <label className="overline mb-2 block">Approx. guest count</label>
                    <Input data-testid="event-guests" type="number" min={1} value={form.guest_count}
                      onChange={(e) => set("guest_count", e.target.value)} className="rounded-2xl border-black/10" />
                  </div>
                </div>
                <Textarea data-testid="event-message" placeholder="Tell us about your celebration (optional)"
                  value={form.message} onChange={(e) => set("message", e.target.value)} className="rounded-2xl border-black/10" />
                <Button data-testid="event-submit" type="submit" className="h-12 w-full rounded-full btn-ink">
                  Send my inquiry <ArrowRight size={16} className="ml-1" />
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
